import { useEffect, useRef, useState } from "react";
import type { StudentTestAnswer, StudentTestContent } from "p6m-p6u";
import { Views } from "../enums/views";
import { convertTimeLimitToSeconds } from "../helpers/DateTime";
import { getIsLocalTestRun, getIsTestRun } from "../helpers/Url";
import { TEST_DATA_KEY_PREFIX } from "../helpers/testDataStorage";
import { PersistedAnswersDecisions } from "../complex/onlineTest/onlineTestMachine";

interface UseTestPersistenceParams {
    teacherTest: StudentTestContent;
    studentTestAnswer: StudentTestAnswer;
    currentMainView: Views | number;
    currentExerciseId: string;
    countdownStartedAt: number;
    isPreview: boolean;
    /** Owner the persisted answers get tied to: the authenticated member on a login-gated activity, null otherwise. */
    persistedAnswersOwnerId: string | null;
    /** Whether a stored record belongs to whoever is asking. Decided by the machine, carried out here. */
    getPersistedAnswersDecision: (storedOwner: string | null) => PersistedAnswersDecisions;
    /**
     * Rehydration cannot start without this: a member that is still `null` reads as a mismatch and
     * discards their own answers, and finishing early pins the write gate to an anonymous owner.
     */
    isClassMemberSessionReady: boolean;
    setStudentTestAnswer: (val: StudentTestAnswer) => void;
    setCurrentMainView: (val: Views | number) => void;
    setCurrentExerciseId: (val: string) => void;
    setCountdownStarted: (val: boolean) => void;
    setCountdownStartedAt: (val: number) => void;
    setTestEndTimestamp: (val: number) => void;
}

interface UseTestPersistenceReturn {
    rehydrated: boolean;
    /**
     * True when rehydration restored a finished attempt (persisted view was OUTRO): the member
     * submitted in an earlier visit. Distinct from "currently on OUTRO", which a just-finished
     * submit also satisfies.
     */
    restoredSubmittedState: boolean;
    clearPersistedAnswers: () => void;
}

/**
 * Combines the persisted `startedAt` with the *current* timeLimit/dueDate, so a teacher's mid-attempt edit
 * takes effect on the next rehydrate. Returns 0 when neither is set — the caller leaves the timer unstarted.
 */
const _deriveEndTimestamp = (
    startedAt: number,
    currentTimeLimit: string | undefined,
    currentDueDate: number | undefined
): number => {
    const timeLimitEnd = currentTimeLimit ? startedAt + convertTimeLimitToSeconds(currentTimeLimit) : null;
    const dueDateEnd = currentDueDate ? Math.floor(currentDueDate / 1000) : null;
    const candidates = [timeLimitEnd, dueDateEnd].filter((t): t is number => t !== null);
    if (candidates.length === 0) return 0;
    return Math.min(...candidates);
};

const useTestPersistence = (params: UseTestPersistenceParams): UseTestPersistenceReturn => {
    const [rehydrated, setRehydrated] = useState(false);
    // A separate flag rather than latching `rehydrated`: consumers gated on that (countdown seeding, the
    // answer scaffold) may proceed, while this effect stays re-runnable for the owner's re-login.
    const [holdingForOwner, setHoldingForOwner] = useState(false);
    const [restoredSubmittedState, setRestoredSubmittedState] = useState(false);
    // Ties writes to the identity we rehydrated as, so a logout in another tab cannot re-write the
    // just-wiped answers as an anonymous record.
    const rehydratedOwnerRef = useRef<string | null>(null);

    const clearPersistedAnswers = () => {
        const testId = params.teacherTest?.id;
        if (!testId) return;
        localStorage.removeItem(TEST_DATA_KEY_PREFIX + testId);
    };

    useEffect(() => {
        if ((!rehydrated || holdingForOwner) && params.teacherTest && params.teacherTest.id) {
            const finishRehydration = () => {
                rehydratedOwnerRef.current = params.persistedAnswersOwnerId;
                setHoldingForOwner(false);
                setRehydrated(true);
            };
            // Skipped entirely in preview: rehydrating would drop the author onto whatever screen a
            // student's session of this test ended on — a stored OUTRO even reads as "already submitted".
            if (params.isPreview) {
                finishRehydration();
                return;
            }

            /*
             * Nothing may finish before identity settles, stored record or not: `finishRehydration` pins the
             * write gate to the current owner, and pinning it to `null` stores a member's answers anonymously.
             */
            if (!params.isClassMemberSessionReady) return;

            const test = localStorage.getItem(TEST_DATA_KEY_PREFIX + params.teacherTest.id);
            if (!test) {
                finishRehydration();
                return;
            }
            let parsedTest;
            try {
                parsedTest = JSON.parse(test);
            } catch {
                localStorage.removeItem(TEST_DATA_KEY_PREFIX + params.teacherTest.id);
                finishRehydration();
                return;
            }
            const storedOwner = parsedTest.classMemberId ?? null;
            const decision = params.getPersistedAnswersDecision(storedOwner);

            if (decision === PersistedAnswersDecisions.PENDING) return;

            if (decision === PersistedAnswersDecisions.HOLD) {
                // The ref keeps the current (null) owner, so the write gate stays shut and nobody
                // overwrites the record while its owner is away.
                rehydratedOwnerRef.current = params.persistedAnswersOwnerId;
                setHoldingForOwner(true);
                setRehydrated(true);
                return;
            }

            if (decision === PersistedAnswersDecisions.DISCARD) {
                localStorage.removeItem(TEST_DATA_KEY_PREFIX + params.teacherTest.id);
                finishRehydration();
                return;
            }

            finishRehydration();
            if (parsedTest.studentTestAnswer) {
                params.setStudentTestAnswer(parsedTest.studentTestAnswer);
            }
            if (parsedTest.currentMainView != null) {
                params.setCurrentMainView(parsedTest.currentMainView);
                if (parsedTest.currentMainView === Views.OUTRO) {
                    setRestoredSubmittedState(true);
                }
            }
            if (parsedTest.currentExerciseId) {
                params.setCurrentExerciseId(parsedTest.currentExerciseId);
            }
            if (parsedTest.startedAt > 0) {
                // Always restore `startedAt` so the persist effect doesn't overwrite it with 0 on the
                // next render — even when both timing fields were temporarily cleared by the teacher.
                params.setCountdownStartedAt(parsedTest.startedAt);
                const derivedEnd = _deriveEndTimestamp(
                    parsedTest.startedAt,
                    params.teacherTest.timeLimit,
                    params.teacherTest.dueDate
                );
                // Both fields cleared → leave countdown unstarted; useCountdown will re-seed when needed.
                if (derivedEnd > 0) {
                    params.setCountdownStarted(true);
                    params.setTestEndTimestamp(derivedEnd);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        params.teacherTest,
        params.isPreview,
        params.isClassMemberSessionReady,
        params.persistedAnswersOwnerId,
        // A held record is only released by re-asking, so the decision has to be able to re-trigger this on
        // its own — the owner id it is derived from may already have settled a render earlier.
        params.getPersistedAnswersDecision,
        rehydrated,
        holdingForOwner,
    ]);

    useEffect(() => {
        if (
            rehydrated &&
            // Never write while holding someone else's record: nothing was loaded, so this would replace
            // their answers with an empty state.
            !holdingForOwner &&
            params.persistedAnswersOwnerId === rehydratedOwnerRef.current &&
            params.teacherTest &&
            params.teacherTest.id &&
            !params.isPreview &&
            !getIsTestRun() &&
            !getIsLocalTestRun()
        ) {
            localStorage.setItem(
                TEST_DATA_KEY_PREFIX + params.teacherTest.id,
                JSON.stringify({
                    studentTestAnswer: params.studentTestAnswer,
                    currentMainView: params.currentMainView,
                    currentExerciseId: params.currentExerciseId,
                    // Owner of these answers, checked on rehydrate. The stored key keeps its historical
                    // name so existing records stay readable.
                    classMemberId: params.persistedAnswersOwnerId,
                    // Unix seconds at which the countdown was first seeded. Used on rehydrate to re-derive
                    // testEndTimestamp from the *current* timeLimit/dueDate, so teacher edits land cleanly.
                    startedAt: params.countdownStartedAt,
                })
            );
        }
    }, [
        params.studentTestAnswer,
        params.currentMainView,
        params.currentExerciseId,
        params.teacherTest,
        rehydrated,
        holdingForOwner,
        params.countdownStartedAt,
        params.isPreview,
        params.persistedAnswersOwnerId,
    ]);

    return { rehydrated, restoredSubmittedState, clearPersistedAnswers };
};

export default useTestPersistence;
